import { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Container,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  LibraryBooks,
  Search,
  Category,
  Article,
  VideoLibrary,
  Description,
  PictureAsPdf,
  GetApp,
  Visibility,
  Book,
  School,
  Gavel,
  Business,
  AccountBalance,
  MenuBook,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import {
  useListKnowledgeLibraryQuery,
  useCreateKnowledgeLibraryItemMutation,
  useDeleteKnowledgeLibraryItemMutation,
  useLazyDownloadKnowledgeLibraryItemQuery,
} from '../store/api/knowledgeLibraryApi'
import { PageHeader } from '../components/PageHeader'
import { getCardSurfaceSx, getContentPaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'

// Dummy data (danh mục trang trí — số liệu lấy từ API khi có tài liệu)
const dummyCategories = [
  { id: '1', name: 'Pháp luật Dân sự', icon: <Gavel />, count: 45, color: 'primary' },
  { id: '2', name: 'Pháp luật Thương mại', icon: <Business />, count: 38, color: 'secondary' },
  { id: '3', name: 'Pháp luật Hình sự', icon: <AccountBalance />, count: 32, color: 'error' },
  { id: '4', name: 'Pháp luật Lao động', icon: <School />, count: 28, color: 'info' },
  { id: '5', name: 'Tài liệu tham khảo', icon: <MenuBook />, count: 67, color: 'success' },
]

const dummyDocuments = [
  {
    id: '1',
    title: 'Bộ luật Dân sự 2015 - Toàn văn',
    category: 'Pháp luật Dân sự',
    type: 'pdf',
    size: '2.5 MB',
    uploadDate: '2024-01-15',
    views: 245,
    downloads: 89,
    description: 'Toàn văn Bộ luật Dân sự năm 2015, bao gồm tất cả các điều khoản và quy định hiện hành.',
    tags: ['Dân sự', 'Bộ luật', 'Quy định'],
  },
  {
    id: '2',
    title: 'Hướng dẫn soạn thảo hợp đồng dân sự',
    category: 'Pháp luật Dân sự',
    type: 'pdf',
    size: '1.8 MB',
    uploadDate: '2024-02-10',
    views: 189,
    downloads: 67,
    description: 'Tài liệu hướng dẫn chi tiết về cách soạn thảo các loại hợp đồng dân sự phổ biến.',
    tags: ['Hợp đồng', 'Hướng dẫn', 'Mẫu'],
  },
  {
    id: '3',
    title: 'Luật Thương mại 2005 - Cập nhật 2023',
    category: 'Pháp luật Thương mại',
    type: 'pdf',
    size: '3.2 MB',
    uploadDate: '2024-03-05',
    views: 312,
    downloads: 124,
    description: 'Luật Thương mại với các điều khoản đã được sửa đổi, bổ sung đến năm 2023.',
    tags: ['Thương mại', 'Cập nhật', 'Luật'],
  },
  {
    id: '4',
    title: 'Video bài giảng: Phân tích án lệ dân sự',
    category: 'Pháp luật Dân sự',
    type: 'video',
    size: '125 MB',
    uploadDate: '2024-01-20',
    views: 456,
    downloads: 234,
    description: 'Video bài giảng phân tích các án lệ quan trọng trong lĩnh vực dân sự, do GS. Nguyễn Văn A trình bày.',
    tags: ['Video', 'Án lệ', 'Bài giảng'],
  },
  {
    id: '5',
    title: 'Bộ luật Hình sự 2015 - Sửa đổi 2022',
    category: 'Pháp luật Hình sự',
    type: 'pdf',
    size: '2.9 MB',
    uploadDate: '2024-02-28',
    views: 278,
    downloads: 98,
    description: 'Bộ luật Hình sự với các điều khoản đã được sửa đổi, bổ sung theo Luật số 12/2022/QH15.',
    tags: ['Hình sự', 'Bộ luật', 'Sửa đổi'],
  },
  {
    id: '6',
    title: 'Tài liệu tham khảo: Các vụ án nổi tiếng',
    category: 'Tài liệu tham khảo',
    type: 'pdf',
    size: '4.1 MB',
    uploadDate: '2024-03-15',
    views: 389,
    downloads: 156,
    description: 'Tuyển tập các vụ án nổi tiếng trong lịch sử pháp luật Việt Nam, có phân tích chi tiết.',
    tags: ['Án lệ', 'Tham khảo', 'Lịch sử'],
  },
  {
    id: '7',
    title: 'Video: Kỹ năng tranh tụng tại tòa',
    category: 'Tài liệu tham khảo',
    type: 'video',
    size: '98 MB',
    uploadDate: '2024-01-30',
    views: 567,
    downloads: 289,
    description: 'Video hướng dẫn các kỹ năng tranh tụng hiệu quả tại tòa án, bao gồm cách trình bày và phản biện.',
    tags: ['Video', 'Kỹ năng', 'Tranh tụng'],
  },
  {
    id: '8',
    title: 'Luật Lao động 2019 - Toàn văn',
    category: 'Pháp luật Lao động',
    type: 'pdf',
    size: '2.1 MB',
    uploadDate: '2024-02-20',
    views: 201,
    downloads: 76,
    description: 'Toàn văn Luật Lao động năm 2019, bao gồm các quy định về hợp đồng lao động, tiền lương, bảo hiểm.',
    tags: ['Lao động', 'Luật', 'Quy định'],
  },
]

const dummyRecentUploads = [
  { id: '1', title: 'Bộ luật Dân sự 2015 - Toàn văn', date: '2024-01-15', category: 'Pháp luật Dân sự' },
  { id: '2', title: 'Hướng dẫn soạn thảo hợp đồng dân sự', date: '2024-02-10', category: 'Pháp luật Dân sự' },
  { id: '3', title: 'Luật Thương mại 2005 - Cập nhật 2023', date: '2024-03-05', category: 'Pháp luật Thương mại' },
]

const dummyPopularDocs = [
  { id: '4', title: 'Video bài giảng: Phân tích án lệ dân sự', views: 456, category: 'Pháp luật Dân sự' },
  { id: '7', title: 'Video: Kỹ năng tranh tụng tại tòa', views: 567, category: 'Tài liệu tham khảo' },
  { id: '3', title: 'Luật Thương mại 2005 - Cập nhật 2023', views: 312, category: 'Pháp luật Thương mại' },
]

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function KnowledgeLibraryPage() {
  const { user } = useAppSelector((s) => s.auth)
  const canManage = user && hasPermission(user.role, 'ASSIGN_CASES')
  const { data: apiItems = [], isLoading } = useListKnowledgeLibraryQuery()
  const [createItem, { isLoading: creating }] = useCreateKnowledgeLibraryItemMutation()
  const [deleteItem] = useDeleteKnowledgeLibraryItemMutation()
  const [downloadItem] = useLazyDownloadKnowledgeLibraryItemQuery()

  const libraryDocs = useMemo(
    () =>
      apiItems
        .filter((k) => k.id)
        .map((k) => ({
          id: k.id as string,
          title: k.title,
          category: k.category || 'general',
          type: k.mime_type?.includes('pdf') ? 'pdf' : 'video',
          size:
            k.file_size >= 1024 * 1024
              ? `${(k.file_size / 1024 / 1024).toFixed(2)} MB`
              : `${Math.max(1, Math.round(k.file_size / 1024))} KB`,
          uploadDate: k.created_at || '',
          views: 0,
          downloads: 0,
          description: k.description || '—',
          tags: k.tags || [],
        })),
    [apiItems],
  )

  const categoryCounts = useMemo(() => {
    const m = new Map<string, number>()
    libraryDocs.forEach((d) => m.set(d.category, (m.get(d.category) || 0) + 1))
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [libraryDocs])

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [tabValue, setTabValue] = useState(0)
  const [selectedDoc, setSelectedDoc] = useState<any>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)

  const filteredDocs = libraryDocs.filter((doc) => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const recentDocs = useMemo(() => {
    return [...libraryDocs].sort((a, b) => (b.uploadDate || '').localeCompare(a.uploadDate || '')).slice(0, 10)
  }, [libraryDocs])

  const handleViewDetail = (doc: any) => {
    setSelectedDoc(doc)
    setDetailDialogOpen(true)
  }

  const getTypeIcon = (type: string) => {
    return type === 'video' ? <VideoLibrary /> : <PictureAsPdf />
  }

  const getTypeColor = (type: string) => {
    return type === 'video' ? 'error' : 'primary'
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Kho học liệu"
        subtitle="Tài liệu tham khảo, bài giảng và văn bản pháp luật phục vụ luyện tập tranh tụng."
        icon={<LibraryBooks />}
        mb={4}
        actions={
          canManage ? (
            <Button variant="contained" color="primary" component="label" disabled={creating}>
              Tải lên tài liệu
              <input
                type="file"
                hidden
                accept=".pdf,application/pdf"
                onChange={async (e) => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (!file) return
                  const fd = new FormData()
                  fd.append('title', file.name.replace(/\.[^/.]+$/, ''))
                  fd.append('category', 'general')
                  fd.append('file', file)
                  try {
                    await createItem(fd).unwrap()
                  } catch (err: any) {
                    alert(err?.data?.detail || err?.message || 'Upload thất bại')
                  }
                }}
              />
            </Button>
          ) : null
        }
      />

      {isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Đang tải danh sách…
        </Typography>
      )}

      {/* Search Bar */}
      <Paper elevation={0} sx={(theme) => ({ ...getFilterPaperSx(theme), mb: 4 })}>
        <TextField
          fullWidth
          placeholder="Tìm kiếm tài liệu, video, bài giảng..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Categories */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
          Danh mục
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Chip
            label="Tất cả"
            onClick={() => setSelectedCategory('all')}
            color={selectedCategory === 'all' ? 'primary' : 'default'}
            sx={{ height: 40, fontSize: '0.95rem' }}
          />
          {categoryCounts.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              Thêm tài liệu để hiển thị danh mục theo trường category.
            </Typography>
          )}
          {categoryCounts.map(([name, count]) => (
            <Chip
              key={name}
              label={`${name} (${count})`}
              onClick={() => setSelectedCategory(name)}
              color={selectedCategory === name ? 'primary' : 'default'}
              sx={{ height: 40, fontSize: '0.95rem' }}
            />
          ))}
        </Box>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 0, mb: 4 })}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Tất cả tài liệu" />
          <Tab label="Tải lên gần đây" />
          <Tab label="Phổ biến nhất" />
        </Tabs>
      </Paper>

      {/* All Documents */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {filteredDocs.length === 0 && !isLoading && (
            <Grid item xs={12}>
              <Typography color="text.secondary">Chưa có tài liệu hoặc không khớp bộ lọc.</Typography>
            </Grid>
          )}
          {filteredDocs.map((doc) => (
            <Grid item xs={12} sm={6} md={4} key={doc.id}>
              <Card
                elevation={0}
                sx={(theme) => ({
                  ...getCardSurfaceSx(theme),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                })}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${getTypeColor(doc.type)}.main`, width: 48, height: 48 }}>
                      {getTypeIcon(doc.type)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {doc.title}
                      </Typography>
                      <Chip label={doc.category} size="small" sx={{ mb: 1 }} />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                    {doc.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {doc.tags.map((tag, idx) => (
                      <Chip key={idx} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {doc.size}
                      {doc.uploadDate ? ` • ${new Date(doc.uploadDate).toLocaleDateString('vi-VN')}` : ''}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => handleViewDetail(doc)}
                  >
                    Xem chi tiết
                  </Button>
                  <Button
                    size="small"
                    startIcon={<GetApp />}
                    color="primary"
                    onClick={async () => {
                      try {
                        const blob = await downloadItem(doc.id).unwrap()
                        const url = window.URL.createObjectURL(blob)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = `${doc.title}.pdf`
                        document.body.appendChild(a)
                        a.click()
                        window.URL.revokeObjectURL(url)
                        document.body.removeChild(a)
                      } catch (err: any) {
                        alert(err?.data?.detail || err?.message || 'Tải thất bại')
                      }
                    }}
                  >
                    Tải xuống
                  </Button>
                  {canManage && (
                    <Button
                      size="small"
                      color="error"
                      onClick={async () => {
                        if (!window.confirm('Xóa tài liệu này?')) return
                        try {
                          await deleteItem(doc.id).unwrap()
                        } catch (err: any) {
                          alert(err?.data?.detail || err?.message)
                        }
                      }}
                    >
                      Xóa
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Recent Uploads */}
      <TabPanel value={tabValue} index={1}>
        <List>
          {recentDocs.map((doc, idx) => (
              <Box key={doc.id}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton edge="end" onClick={() => handleViewDetail(doc)}>
                        <Visibility />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={async () => {
                          try {
                            const blob = await downloadItem(doc.id).unwrap()
                            const url = window.URL.createObjectURL(blob)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = `${doc.title}.pdf`
                            document.body.appendChild(a)
                            a.click()
                            window.URL.revokeObjectURL(url)
                            document.body.removeChild(a)
                          } catch (err: any) {
                            alert(err?.data?.detail || err?.message)
                          }
                        }}
                      >
                        <GetApp />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: `${getTypeColor(doc.type || 'pdf')}.main` }}>
                      {getTypeIcon(doc.type || 'pdf')}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={doc.title}
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {doc.category}
                          {doc.uploadDate ? ` • ${new Date(doc.uploadDate).toLocaleDateString('vi-VN')}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {idx < recentDocs.length - 1 && <Divider />}
              </Box>
            ))}
        </List>
      </TabPanel>

      {/* Popular Documents */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {recentDocs.slice(0, 6).map((doc) => {
            return (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: `${getTypeColor(doc.type || 'pdf')}.main`, width: 48, height: 48 }}>
                        {getTypeIcon(doc.type || 'pdf')}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {doc.title}
                        </Typography>
                        <Chip label={doc.category} size="small" />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        icon={<Visibility />} 
                        label="Mở chi tiết" 
                        size="small" 
                        color="primary"
                      />
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" startIcon={<Visibility />} onClick={() => handleViewDetail(doc)}>
                      Xem chi tiết
                    </Button>
                    <Button
                      size="small"
                      startIcon={<GetApp />}
                      color="primary"
                      onClick={async () => {
                        try {
                          const blob = await downloadItem(doc.id).unwrap()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = `${doc.title}.pdf`
                          document.body.appendChild(a)
                          a.click()
                          window.URL.revokeObjectURL(url)
                          document.body.removeChild(a)
                        } catch (err: any) {
                          alert(err?.data?.detail || err?.message)
                        }
                      }}
                    >
                      Tải xuống
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </TabPanel>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedDoc && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${getTypeColor(selectedDoc.type)}.main` }}>
                  {getTypeIcon(selectedDoc.type)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{selectedDoc.title}</Typography>
                  <Chip label={selectedDoc.category} size="small" sx={{ mt: 0.5 }} />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedDoc.description}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {selectedDoc.tags.map((tag: string, idx: number) => (
                  <Chip key={idx} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Kích thước:</strong> {selectedDoc.size}
                </Typography>
                <Typography variant="body2">
                  <strong>Ngày tải lên:</strong>{' '}
                  {selectedDoc.uploadDate
                    ? new Date(selectedDoc.uploadDate).toLocaleDateString('vi-VN')
                    : '—'}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>
                Đóng
              </Button>
              <Button
                variant="contained"
                startIcon={<GetApp />}
                onClick={async () => {
                  if (!selectedDoc?.id) return
                  try {
                    const blob = await downloadItem(selectedDoc.id).unwrap()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${selectedDoc.title}.pdf`
                    document.body.appendChild(a)
                    a.click()
                    window.URL.revokeObjectURL(url)
                    document.body.removeChild(a)
                  } catch (err: any) {
                    alert(err?.data?.detail || err?.message)
                  }
                }}
              >
                Tải xuống
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  )
}
